/**
 * Viewport math (screen = image × s + t). `zoomAt` implements the FIXED clamped-factor rule
 * (original: `zoomAtOriginal`). `fitScaleOf` and `clampTransform` are verbatim
 * from the original `features/editor/viewport.ts` (minus `'worklet'`); the rest is the logic
 * of the `useViewport` hook and the pan/pinch/wheel handlers in `CanvasView.tsx`, extracted
 * into pure functions over plain values (no shared values, no React).
 */

export const PADDING = 12;
export const MAX_ZOOM = 16;
export const MIN_ZOOM = 0.6;
/** Animated fit duration (each of s, tx, ty animates independently, easing out-cubic). */
export const FIT_ANIMATION_MS = 260;
/** Keyboard zoom factors (⌘+ / ⌘−). */
export const ZOOM_IN_FACTOR = 1.25;
export const ZOOM_OUT_FACTOR = 0.8;

/** Scale that fits the photo in the view with a small margin. */
export function fitScaleOf(viewW: number, viewH: number, imgW: number, imgH: number): number {
  if (viewW <= 0 || viewH <= 0 || imgW <= 0 || imgH <= 0) return 1;
  return Math.min((viewW - PADDING * 2) / imgW, (viewH - PADDING * 2) / imgH);
}

/**
 * Clamps a transform (screen = image * s + t) so zoom stays in range and at least a third of
 * the view keeps showing the photo. Runs on either thread.
 */
export function clampTransform(
  ns: number,
  ntx: number,
  nty: number,
  viewW: number,
  viewH: number,
  imgW: number,
  imgH: number,
): [number, number, number] {
  const k = fitScaleOf(viewW, viewH, imgW, imgH);
  const s = Math.max(k * MIN_ZOOM, Math.min(k * MAX_ZOOM, ns));
  const w = imgW * s;
  const h = imgH * s;
  const minX = Math.min(viewW / 3 - w, (viewW - w) / 2);
  const maxX = Math.max((viewW * 2) / 3, (viewW - w) / 2);
  const minY = Math.min(viewH / 3 - h, (viewH - h) / 2);
  const maxY = Math.max((viewH * 2) / 3, (viewH - h) / 2);
  return [s, Math.max(minX, Math.min(maxX, ntx)), Math.max(minY, Math.min(maxY, nty))];
}

/* ------------------------------------------------------------------ plain-value API */

export interface Transform {
  s: number;
  tx: number;
  ty: number;
}

/** View (canvas stage) size and image size. */
export interface ViewGeometry {
  viewW: number;
  viewH: number;
  imgW: number;
  imgH: number;
}

/** `fit()`: the fitted, centered transform (target of the animated fit too). */
export function fitTransform(g: ViewGeometry): Transform {
  const k = fitScaleOf(g.viewW, g.viewH, g.imgW, g.imgH);
  return { s: k, tx: (g.viewW - g.imgW * k) / 2, ty: (g.viewH - g.imgH * k) / 2 };
}

/** `setTransform(s, tx, ty)`: always clamped. */
export function setTransform(ns: number, ntx: number, nty: number, g: ViewGeometry): Transform {
  const [s, tx, ty] = clampTransform(ns, ntx, nty, g.viewW, g.viewH, g.imgW, g.imgH);
  return { s, tx, ty };
}

/**
 * `zoomAt(factor, fx, fy)`: zoom about a screen point. FIXED behavior (known issue 07 §B6): the
 * translation uses the CLAMPED scale, so the image point under (fx, fy) stays put even at the
 * zoom limits. The original used the unclamped factor (`zoomAtOriginal`).
 */
export function zoomAt(t: Transform, factor: number, fx: number, fy: number, g: ViewGeometry): Transform {
  const [s] = clampTransform(t.s * factor, t.tx, t.ty, g.viewW, g.viewH, g.imgW, g.imgH);
  const k = s / t.s;
  return setTransform(s, fx - (fx - t.tx) * k, fy - (fy - t.ty) * k, g);
}

/** ORIGINAL `zoomAt`: k = (s·factor)/s (unclamped), so the image drifts at the zoom limits. */
export function zoomAtOriginal(t: Transform, factor: number, fx: number, fy: number, g: ViewGeometry): Transform {
  const k = (t.s * factor) / t.s;
  return setTransform(t.s * factor, fx - (fx - t.tx) * k, fy - (fy - t.ty) * k, g);
}

export type ZoomAtFn = typeof zoomAt;

/** `zoomBy(factor)`: zoom about the view center. */
export function zoomBy(t: Transform, factor: number, g: ViewGeometry, zoom: ZoomAtFn = zoomAt): Transform {
  return zoom(t, factor, g.viewW / 2, g.viewH / 2, g);
}

export function toImage(t: Transform, sx: number, sy: number): { x: number; y: number } {
  return { x: (sx - t.tx) / t.s, y: (sy - t.ty) / t.s };
}

export function toScreen(t: Transform, ix: number, iy: number): { x: number; y: number } {
  return { x: ix * t.s + t.tx, y: iy * t.s + t.ty };
}

/**
 * View resize (the layout effect in `useViewport`). `prev` is the previous view size
 * ({0,0} before the first layout or after a photo change). Returns null when the new size
 * or the image is empty (the original returns early and keeps the transform).
 * - first size, or current scale within 1% of the previous fit scale → fit
 * - else keep the image point at the old view center at the new view center (clamped).
 */
export function resizeTransform(
  t: Transform,
  prev: { width: number; height: number },
  next: { width: number; height: number },
  imgW: number,
  imgH: number,
): Transform | null {
  const { width, height } = next;
  if (!width || !height || !imgW || !imgH) return null;
  const prevFit = fitScaleOf(prev.width, prev.height, imgW, imgH);
  if (!prev.width || Math.abs(t.s - prevFit) < prevFit * 0.01) {
    return fitTransform({ viewW: width, viewH: height, imgW, imgH });
  }
  const cx = (prev.width / 2 - t.tx) / t.s;
  const cy = (prev.height / 2 - t.ty) / t.s;
  const [s, tx, ty] = clampTransform(t.s, width / 2 - cx * t.s, height / 2 - cy * t.s, width, height, imgW, imgH);
  return { s, tx, ty };
}

/* ------------------------------------------------------------- pan + pinch gestures */

/** Gesture bookkeeping shared by pan and pinch in `CanvasView` (view-mode fields only). */
export interface GestureState {
  s0: number;
  tx0: number;
  ty0: number;
  panX: number;
  panY: number;
  baseX: number;
  baseY: number;
  pinch: number;
  pinchBase: number;
  fx: number;
  fy: number;
}

export const INITIAL_GESTURE: GestureState = {
  s0: 1, tx0: 0, ty0: 0, panX: 0, panY: 0, baseX: 0, baseY: 0, pinch: 1, pinchBase: 1, fx: 0, fy: 0,
};

/** `rebase()`: the current transform and pan/pinch become the new baseline. */
export function rebase(st: GestureState, t: Transform): GestureState {
  return { ...st, s0: t.s, tx0: t.tx, ty0: t.ty, baseX: st.panX, baseY: st.panY, pinchBase: st.pinch };
}

/** `applyView()`: compose pinch (about the focal point captured at pinch start) and pan. */
export function applyView(st: GestureState, g: ViewGeometry): Transform {
  const k = st.pinch / st.pinchBase;
  const [s, tx, ty] = clampTransform(
    st.s0 * k,
    st.fx - (st.fx - st.tx0) * k + (st.panX - st.baseX),
    st.fy - (st.fy - st.ty0) * k + (st.panY - st.baseY),
    g.viewW,
    g.viewH,
    g.imgW,
    g.imgH,
  );
  return { s, tx, ty };
}

/**
 * View-mode gesture events (pan uses the AVERAGE translation of all touches). Vertex drags and
 * freehand strokes are separate modes and ignore these; pinch is ignored while dragging a vertex.
 */
export type ViewGestureEvent =
  | { type: 'panBegin' }
  | { type: 'panStart' }
  | { type: 'panUpdate'; translationX: number; translationY: number }
  | { type: 'pinchStart'; focalX: number; focalY: number }
  | { type: 'pinchUpdate'; scale: number }
  | { type: 'pinchEnd' };

/** Applies one gesture event; returns the new gesture state and transform. */
export function viewGesture(
  st: GestureState,
  t: Transform,
  e: ViewGestureEvent,
  g: ViewGeometry,
): { gesture: GestureState; transform: Transform } {
  switch (e.type) {
    case 'panBegin':
      return { gesture: { ...st, panX: 0, panY: 0, baseX: 0, baseY: 0 }, transform: t };
    case 'panStart':
      return { gesture: rebase(st, t), transform: t };
    case 'panUpdate': {
      const next = { ...st, panX: e.translationX, panY: e.translationY };
      return { gesture: next, transform: applyView(next, g) };
    }
    case 'pinchStart':
      return { gesture: rebase({ ...st, pinch: 1, pinchBase: 1, fx: e.focalX, fy: e.focalY }, t), transform: t };
    case 'pinchUpdate': {
      const next = { ...st, pinch: e.scale };
      return { gesture: next, transform: applyView(next, g) };
    }
    case 'pinchEnd':
      return { gesture: rebase({ ...st, pinch: 1, pinchBase: 1 }, t), transform: t };
  }
}

/* -------------------------------------------------------------- web wheel / trackpad */

export interface WheelInput {
  deltaX: number;
  deltaY: number;
  /** DOM WheelEvent.deltaMode (0 = pixels). */
  deltaMode: number;
  ctrlKey: boolean;
  /** Pointer position relative to the canvas container (clientX − rect.left, clientY − rect.top). */
  x: number;
  y: number;
}

/** Trackpad pinch arrives as ctrl+wheel; two-finger scroll pans; a mouse wheel zooms. */
export function isTrackpadPan(e: Pick<WheelInput, 'deltaX' | 'deltaY' | 'deltaMode' | 'ctrlKey'>): boolean {
  return !e.ctrlKey && (Math.abs(e.deltaX) > 0 || (e.deltaMode === 0 && Math.abs(e.deltaY) < 40));
}

export function wheelTransform(t: Transform, e: WheelInput, g: ViewGeometry, zoom: ZoomAtFn = zoomAt): Transform {
  if (isTrackpadPan(e)) return setTransform(t.s, t.tx - e.deltaX, t.ty - e.deltaY, g);
  return zoom(t, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0022)), e.x, e.y, g);
}
